import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Eye, Edit, Trash2, Lock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { getUnitLabel } from '@/types/unit-types';
import EntityActions, { EntityAction } from '@/components/shared/EntityActions';
import { formatCurrency, cn } from '@/lib/utils';

interface LineItem {
  id: string;
  item_type?: 'internal' | 'system';
  name?: string;
  title?: string;
  description?: string;
  base_unit?: string;
  unit_symbol?: string;
  final_price?: number | null;
  base_cost?: number;
  category?: string | null;
  category_name?: string | null;
  trade?: string | null;
  trade_name?: string | null;
  tags?: string[] | null;
  is_active?: boolean;
  image_urls?: string[];
  price_last_updated_at?: string | null;
}

interface InternalLineItemsCardViewProps {
  items: LineItem[];
  onItemClick: (item: LineItem) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}


const InternalLineItemsCardView: React.FC<InternalLineItemsCardViewProps> = ({
  items,
  onItemClick,
  onEdit,
  onDelete,
}) => {
  const getLineItemActions = (item: LineItem): EntityAction[] => [
    { label: "View", icon: Eye, onClick: () => onItemClick(item) },
    { label: "Edit", icon: Edit, onClick: () => onEdit(item.id) },
    { 
      label: "Delete", 
      icon: Trash2, 
      onClick: () => onDelete(item.id),
      destructive: true,
      requiresConfirmation: {
        title: "Delete Line Item?",
        description: `Are you sure you want to delete "${item.title || 'this item'}"? This action cannot be undone.`,
      },
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {items.map((item) => (
        <Card 
          key={item.id} 
          className="hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
          onClick={() => onItemClick(item)}
        >
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 min-w-0">
                  <h3 className="font-semibold truncate">
                    {item.name || item.title || 'Untitled'}
                  </h3>
                  {item.item_type === 'system' ? (
                    <Badge className="bg-teal-100 text-teal-800 border-transparent hover:bg-teal-100 text-[10px] px-1.5 py-0">
                      System
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                      Internal
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                  {item.description}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                {item.item_type === 'system' ? (
                  <Lock className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <EntityActions actions={getLineItemActions(item)} size="sm" />
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {/* Pricing */}
              <div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {item.unit_symbol || getUnitLabel(item.base_unit || 'item')}
                  </span>
                  <span className="font-medium">
                    {item.final_price 
                      ? formatCurrency(item.final_price) 
                      : <span className="text-muted-foreground italic">Not calculated</span>
                    }
                  </span>
                </div>
                {item.price_last_updated_at && (
                  <div className={cn(
                    'text-[10px] text-right mt-0.5',
                    Date.now() - new Date(item.price_last_updated_at).getTime() > 90 * 24 * 60 * 60 * 1000
                      ? 'text-amber-600'
                      : 'text-muted-foreground'
                  )}>
                    Updated {formatDistanceToNow(
                      new Date(item.price_last_updated_at),
                      { addSuffix: true }
                    )}
                  </div>
                )}
              </div>

              {/* Category & Trade */}
              {(item.category_name || item.category || item.trade_name || item.trade) && (
                <div className="flex flex-wrap gap-1">
                  {(item.category_name || item.category) && (
                    <Badge variant="secondary" className="text-xs">
                      {item.category_name || item.category}
                    </Badge>
                  )}
                  {(item.trade_name || item.trade) && (
                    <Badge variant="outline" className="text-xs">
                      {item.trade_name || item.trade}
                    </Badge>
                  )}
                </div>
              )}

              {/* Tags */}
              {item.tags && item.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {item.tags.slice(0, 3).map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                  {item.tags.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{item.tags.length - 3}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default InternalLineItemsCardView;
